import { useMemo, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "api";
import { useFetchStateHelper, useMergedFetchState } from "hooks/useFetchState";
import { USER_ROLES } from "constants/userRoles";

const INVOICE_POLL_INTERVAL_MS = 2000;
const INVOICE_WAIT_MS = 30000;

// The invoice is issued by a queued Celery task after the subscription is
// activated, and latestInvoiceId is the farmer's newest invoice of any kind —
// so the invoice is only this payment's once the id differs from the one the
// row had before paying.
const hasNewInvoice = (farmer, previousInvoiceId) =>
  Boolean(farmer?.latestInvoiceId) && farmer.latestInvoiceId !== previousInvoiceId;

// Holds until every given farmer has a new invoice (or the wait runs out),
// then writes the list to the cache once so the amount, status and invoice
// appear together.
const refreshFarmersUntilInvoiced = async (queryClient, userIds) => {
  const findFarmer = (farmers, userId) =>
    farmers?.find((row) => row.id === userId);
  const cachedFarmers = queryClient.getQueryData(["my-farmers"]);
  const previousInvoiceIds = Object.fromEntries(
    userIds.map((userId) => [
      userId,
      findFarmer(cachedFarmers, userId)?.latestInvoiceId,
    ])
  );
  const allInvoiced = (farmers) =>
    userIds.every((userId) =>
      hasNewInvoice(findFarmer(farmers, userId), previousInvoiceIds[userId])
    );

  const deadline = Date.now() + INVOICE_WAIT_MS;
  let farmers = await api.users.fetchMyFarmers();
  while (!allInvoiced(farmers) && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, INVOICE_POLL_INTERVAL_MS));
    farmers = await api.users.fetchMyFarmers();
  }
  queryClient.setQueryData(["my-farmers"], farmers);
};

export const useKydPaymentActions = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: checkout, ...checkoutResponse } = useMutation({
    mutationKey: ["kyd-payment-checkout"],
    mutationFn: ({ userId, tier }) => api.payments.kydCheckout(userId, tier),
  });
  const { fetchState: checkoutFetchState } =
    useFetchStateHelper(checkoutResponse);

  // A cash payment activates the subscription synchronously but the invoice
  // is issued by a queued Celery task (unpaid when the cash is still to be
  // received), so refreshing the list right away would show the farmer as
  // paid without the invoice icon — hold the mutation open until the invoice
  // shows up. On timeout the paid row is still applied.
  const { mutateAsync: cashPayment, ...cashResponse } = useMutation({
    mutationKey: ["kyd-payment-cash"],
    mutationFn: ({ userId, tier, cashReceived = true }) =>
      api.payments.kydCashPayment(userId, tier, cashReceived),
    onSuccess: async (_, { userId }) => {
      await refreshFarmersUntilInvoiced(queryClient, [userId]);
      queryClient.invalidateQueries({ queryKey: ["kyd-cash-balance"] });
    },
  });
  const { fetchState: cashFetchState } = useFetchStateHelper(cashResponse);

  // Marks cash payments as received: { farmers: [{ userId, orderId }] }. Their
  // invoices already exist, so the list is just refreshed.
  const { mutateAsync: confirmCash, ...confirmCashResponse } = useMutation({
    mutationKey: ["kyd-payment-cash-confirm"],
    mutationFn: ({ farmers }) =>
      api.payments.kydConfirmCashPayments(farmers.map(({ orderId }) => orderId)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["my-farmers"] });
      queryClient.invalidateQueries({ queryKey: ["kyd-cash-balance"] });
    },
  });
  const { fetchState: confirmCashFetchState } =
    useFetchStateHelper(confirmCashResponse);


  // The code exists as soon as the POST returns (no invoice until it is paid),
  // so there is no race — refresh the list so the row shows "RF pending" and
  // the modal switches to the pending view. The returned promise keeps the
  // mutation loading until the row has updated.
  const { mutateAsync: createRfCode, ...rfResponse } = useMutation({
    mutationKey: ["kyd-payment-rf-code"],
    mutationFn: ({ userId, sendSms, description, phone }) =>
      api.payments.kydRfCode(userId, { sendSms, description, phone }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-farmers"] }),
  });
  const { fetchState: rfFetchState } = useFetchStateHelper(rfResponse);

  const { mutateAsync: resendRfSms, ...resendResponse } = useMutation({
    mutationKey: ["kyd-payment-rf-resend-sms"],
    mutationFn: ({ userId, phone }) =>
      api.payments.kydResendRfSms(userId, phone),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-farmers"] }),
  });
  const { fetchState: resendFetchState } = useFetchStateHelper(resendResponse);

  const { mutateAsync: cancelRfCode, ...cancelResponse } = useMutation({
    mutationKey: ["kyd-payment-rf-cancel"],
    mutationFn: (rfCodeId) => api.payments.kydCancelRfCode(rfCodeId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-farmers"] }),
  });
  const { fetchState: cancelFetchState } = useFetchStateHelper(cancelResponse);

  const { fetchState } = useMergedFetchState([
    checkoutFetchState,
    cashFetchState,
    confirmCashFetchState,
    rfFetchState,
    resendFetchState,
    cancelFetchState,
  ]);

  return {
    checkout,
    cashPayment,
    confirmCash,
    createRfCode,
    resendRfSms,
    cancelRfCode,
    checkoutFetchState,
    cashFetchState,
    confirmCashFetchState,
    rfFetchState,
    resendFetchState,
    cancelFetchState,
    fetchState,
  };
};

// Marks the given received cash orders as handed over to Camvio.
export const useKydRemitCash = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: remitCash, ...remitResponse } = useMutation({
    mutationKey: ["kyd-cash-remit"],
    mutationFn: (orderIds) => api.payments.kydRemitCash(orderIds),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["kyd-cash-balance"] }),
  });
  const { fetchState } = useFetchStateHelper(remitResponse);

  return { remitCash, remitFetchState: fetchState };
};

// The cash this KYD owes Camvio (received) and the cash still to be collected.
export const useKydCashBalance = () => {
  const queryResponse = useQuery({
    queryKey: ["kyd-cash-balance"],
    queryFn: api.payments.fetchKydCashBalance,
  });
  const { fetchState } = useFetchStateHelper(queryResponse);

  return { cashBalance: queryResponse.data, fetchState };
};

// The farmer's row from the shared ["my-farmers"] cache. The payment modal is
// opened with a snapshot of the row, which goes stale once a code is created,
// resent or cancelled while it's open; staleTime: Infinity keeps this observer
// from refetching the whole list just because the modal mounted. It polls only
// while the code's SMS is pending so it can move to "sent"/"failed" without a
// reload — there's no push channel from the SMS-sending Celery task.
export const useKydFarmer = (farmerId) => {
  const { data: farmers } = useQuery({
    queryKey: ["my-farmers"],
    queryFn: api.users.fetchMyFarmers,
    staleTime: Infinity,
    refetchInterval: (query) =>
      query.state.data?.find((row) => row.id === farmerId)?.pendingRfCode
        ?.smsStatus === "pending"
        ? 3000
        : false,
  });

  return farmers?.find((row) => row.id === farmerId);
};

// The logged-in farmer's own RF code awaiting payment, issued by any KYD.
export const useMyPendingRfCode = () => {
  const queryResponse = useQuery({
    queryKey: ["my-pending-rf-code"],
    queryFn: api.payments.fetchMyPendingRfCode,
  });
  const { fetchState } = useFetchStateHelper(queryResponse);

  return { pendingRfCode: queryResponse.data ?? null, fetchState };
};

// The requesting user's own current active subscription — null (not an
// error) when they don't have one, since GET /my-subscription/ 404s in that
// case and paymentsApi.fetchMySubscription already resolves that to null.
// shouldPoll re-fetches every 2s (e.g. while a checkout tab is open) so a
// freshly-activated subscription is picked up without a manual reload.
export const useMySubscription = (shouldPoll = false) => {
  const queryResponse = useQuery({
    queryKey: ["my-subscription"],
    queryFn: api.payments.fetchMySubscription,
    refetchInterval: shouldPoll ? 2000 : false,
  });
  const { fetchState } = useFetchStateHelper(queryResponse);

  return { subscription: queryResponse.data, fetchState };
};

// Basic/Pro plans for the given self-checkout audience, normalized to the
// same { tier, price, durationDays, features } shape either way: an
// agronomist's plans are fixed Plan rows (GET /plans/?audience=agronomist),
// while a farmer's are dynamic and only priced via the per-tier
// dynamic-comparison endpoint (their own stremmata determine the price).
export const usePlansForAudience = (audience) => {
  const queryResponse = useQuery({
    queryKey: ["plans-for-audience", audience],
    queryFn: async () => {
      if (audience === USER_ROLES.AGRONOMIST) {
        return api.payments.fetchPlans({ audience: USER_ROLES.AGRONOMIST });
      }
      const [basic, pro] = await Promise.all([
        api.payments.fetchDynamicComparison(1),
        api.payments.fetchDynamicComparison(2),
      ]);
      return [basic, pro].map((comparison) => ({
        tier: comparison.tier,
        price: comparison.yourPrice,
        stremmata: comparison.yourStremmata,
        durationDays: 365,
        features: null,
      }));
    },
    enabled: Boolean(audience),
  });
  const { fetchState } = useFetchStateHelper(queryResponse);

  return { plans: queryResponse.data, fetchState };
};

// Starts a self-checkout for the requesting user's own plan — farmer or
// agronomist, picked once up front since a NavUserButton-opened modal only
// ever checks out for the logged-in user's own role.
export const useSelfCheckoutActions = (audience) => {
  const checkoutFn = useMemo(
    () =>
      audience === USER_ROLES.AGRONOMIST
        ? api.payments.agronomistCheckout
        : api.payments.farmerCheckout,
    [audience]
  );

  const { mutateAsync: checkout, ...checkoutResponse } = useMutation({
    mutationKey: ["self-checkout", audience],
    mutationFn: (tier) => checkoutFn(tier),
  });
  const { fetchState: checkoutFetchState } =
    useFetchStateHelper(checkoutResponse);

  return { checkout, checkoutFetchState };
};

// Polls until the given farmer is paid *and* this payment's invoice exists
// (or INVOICE_WAIT_MS has passed since it showed as paid, so a stalled invoice
// worker can't hang it), then writes the list to the ["my-farmers"] cache
// once. It polls under its own key so the dashboard row never shows the
// intermediate "paid, no invoice yet" state. previousInvoiceId is the row's
// latestInvoiceId from before the payment started.
export const useFarmerPaymentStatus = (farmerId, previousInvoiceId) => {
  const queryClient = useQueryClient();
  const paidSinceRef = useRef(null);
  // The row's latestInvoiceId changes once we write the cache below, so the
  // "before" value has to be frozen when polling starts.
  const baselineInvoiceIdRef = useRef(undefined);
  if (farmerId && baselineInvoiceIdRef.current === undefined) {
    baselineInvoiceIdRef.current = previousInvoiceId;
  }

  const isSettled = (farmers) => {
    const current = farmers?.find((row) => row.id === farmerId);
    if (!current?.hasPaidSubscription) return false;
    return (
      hasNewInvoice(current, baselineInvoiceIdRef.current) ||
      Date.now() - paidSinceRef.current > INVOICE_WAIT_MS
    );
  };

  const { data: farmers } = useQuery({
    queryKey: ["farmer-payment-status", farmerId],
    queryFn: async () => {
      const data = await api.users.fetchMyFarmers();
      if (data.find((row) => row.id === farmerId).hasPaidSubscription) {
        paidSinceRef.current ??= Date.now();
      }
      if (isSettled(data)) queryClient.setQueryData(["my-farmers"], data);
      return data;
    },
    enabled: Boolean(farmerId),
    gcTime: 0,
    refetchInterval: (query) =>
      isSettled(query.state.data) ? false : INVOICE_POLL_INTERVAL_MS,
  });

  return { isPaid: isSettled(farmers) };
};
